import { api } from "../../../api/Middleware";
import { CHATS_URL, WABOTTY_URL, AI_URL, USERS_URL, API_URL } from "../../../api/Urls";

/**
 * API Service para Central de Atendimento (multi-canal: WhatsApp + Instagram).
 *
 * Convenções multi-canal:
 *  - `channel`: 'whatsapp' (default, omitido em chamadas legado) ou 'instagram'
 *  - `session`: para WhatsApp = telefone do bot; para Instagram = ig_business_account_id
 *  - `userId`: identificador escopado pelo canal (telefone E.164 para WhatsApp,
 *    PSID/IGSID para Instagram). NÃO normalizar com /\D/ se canal !== 'whatsapp'.
 */

/**
 * Helper: normaliza identificador do usuário conforme o canal.
 * WhatsApp: remove tudo que não é dígito (E.164 sem formatação).
 * Instagram: PSID já vem em formato puro — apenas trim.
 */
const normalizeUserId = (userId, channel) => {
  if (!userId) return '';
  const str = String(userId);
  if (channel === 'instagram') {
    return str.trim();
  }
  return str.replace(/\D/g, '');
};

const ServiceCenterAPI = {
  // ==========================================
  // CONVERSAS
  // ==========================================

  /**
   * Listar conversas com sincronização
   * @param {string} session - Identificador do bot/canal (telefone para WhatsApp,
   *   ig_business_account_id para Instagram)
   * @param {object} params - Parâmetros de paginação e filtros (channel opcional)
   */
  getConversations: async (session, params = {}) => {
    const { channel, ...rest } = params;
    const queryParams = {
      session,
      sync: 1,
      limit: rest.limit || 20,
      offset: rest.offset || 0,
      ...rest,
    };
    if (channel) queryParams.channel = channel;

    const response = await api.get(CHATS_URL, { params: queryParams });
    return response.data;
  },

  /**
   * Lista conversas multi-canal (omnichannel). Mescla WhatsApp + Instagram
   * (e Messenger no futuro). Constraint produto: máx 1 conta por canal.
   *
   * @param {Set<string>|Array<string>} channels - Filtro de canais. Vazio
   *   ou null = todos. Ex: ['whatsapp'] ou Set(['instagram']).
   */
  getMultiChannelConversations: async ({ channels, limit = 30, offset = 0, search } = {}) => {
    const params = { multi: 1, limit, offset };
    const list = channels instanceof Set ? [...channels] : (channels || []);
    if (list.length > 0) {
      params.channels = list.join(",");
    }
    if (search) {
      params.phone = search;
    }
    const response = await api.get(CHATS_URL, { params });
    return response.data;
  },

  /**
   * Buscar mensagens de uma conversa
   * @param {string} session - Identificador do bot/canal
   * @param {string} userId - Identificador do contato (telefone E.164 ou PSID/IGSID)
   * @param {number} offset - Offset para paginação
   * @param {number} limit - Limite de mensagens
   * @param {string} channel - 'whatsapp' (default) ou 'instagram'
   */
  getMessages: async (session, userId, offset = 0, limit = 30, channel = 'whatsapp') => {
    const normalized = normalizeUserId(userId, channel);

    const params = { session, phone: normalized, limit, offset: offset || undefined };
    if (channel && channel !== 'whatsapp') {
      params.channel = channel;
    }

    const response = await api.get(CHATS_URL + `${normalized}/messages/`, { params });
    return response.data;
  },

  /**
   * Enviar mensagem de texto.
   *
   * Para Instagram, o payload deve conter:
   *   { channel: 'instagram', session, user_id, message, message_kind, comment_id? }
   *
   * Para WhatsApp (default), payload tradicional:
   *   { session, phone, message, ... }
   *
   * @param {object} payload - Dados da mensagem
   */
  sendMessage: async (payload) => {
    const response = await api.post(CHATS_URL + "messages/send/", payload);
    return response.data;
  },

  /**
   * Enviar mensagem de áudio
   * @param {FormData} formData - FormData com o áudio
   */
  sendAudioMessage: async (formData) => {
    const response = await api.post(CHATS_URL + "messages/send/", formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    return response.data;
  },

  /**
   * Enviar mensagem de mídia (imagem, vídeo, documento)
   * @param {FormData} formData - FormData com a mídia
   */
  sendMediaMessage: async (formData) => {
    const response = await api.post(CHATS_URL + "messages/send/", formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    return response.data;
  },

  // ==========================================
  // FILA DE ATENDIMENTO
  // ==========================================

  /**
   * Buscar fila de atendimento
   * @param {object} params - Filtros (department, status, attendant, source)
   */
  getServiceQueue: async (params = {}) => {
    const response = await api.get("contacts/service/queue/", {
      params: {
        limit: params.limit || 20,
        offset: params.offset || 0,
        ...params,
      },
    });
    return response.data;
  },

  /**
   * Busca server-side de contatos (Camada 1 do híbrido).
   * Procura por nome, telefone ou whatsapp_id no banco local — cobre
   * todo o histórico do account, não só os N chats da overview do WAHA.
   * Retorna formato Contact (frontend converte pra Conversation).
   * @param {string} query - Termo de busca (mínimo 2 caracteres)
   * @param {object} params - { limit }
   */
  searchContacts: async (query, params = {}) => {
    const response = await api.get("contacts/service/search/", {
      params: {
        q: query,
        limit: params.limit || 20,
      },
    });
    return response.data;
  },

  /**
   * Buscar meus atendimentos
   */
  getMyContacts: async () => {
    const response = await api.get("contacts/service/my-contacts/");
    return response.data;
  },

  // ==========================================
  // AÇÕES DE ATENDIMENTO
  // ==========================================

  /**
   * Iniciar atendimento
   * @param {number} contactId - ID do contato
   */
  startService: async (contactId) => {
    const response = await api.post(
      `contacts/${contactId}/service/start/`
    );
    return response.data;
  },

  /**
   * Transferir atendimento
   * @param {number} contactId - ID do contato
   * @param {object} data - { attendant_id, department_id, reason }
   */
  transferService: async (contactId, data) => {
    const response = await api.post(
      `contacts/${contactId}/service/transfer/`,
      data
    );
    return response.data;
  },

  /**
   * Finalizar atendimento
   * @param {number} contactId - ID do contato
   * @param {object} data - { notes }
   */
  finishService: async (contactId, data = {}) => {
    const response = await api.post(
      `contacts/${contactId}/service/finish/`,
      data
    );
    return response.data;
  },

  /**
   * Atualizar status do contato
   * @param {number} contactId - ID do contato
   * @param {string} status - new, in_service, waiting, finished
   */
  updateServiceStatus: async (contactId, status) => {
    const response = await api.post(
      `contacts/${contactId}/service/update-status/`,
      { status }
    );
    return response.data;
  },

  // ==========================================
  // NOTAS DO CONTATO
  // ==========================================

  /**
   * Listar notas do contato
   * @param {number} contactId - ID do contato
   * @param {object} params - Filtros (type)
   */
  getNotes: async (contactId, params = {}) => {
    const response = await api.get(`contacts/${contactId}/notes/`, {
      params,
    });
    return response.data;
  },

  /**
   * Criar nota
   * @param {number} contactId - ID do contato
   * @param {object} data - { content, note_type, is_internal, is_pinned }
   */
  createNote: async (contactId, data) => {
    const response = await api.post(
      `contacts/${contactId}/notes/`,
      data
    );
    return response.data;
  },

  /**
   * Deletar nota
   * @param {number} contactId - ID do contato
   * @param {number} noteId - ID da nota
   */
  deleteNote: async (contactId, noteId) => {
    const response = await api.delete(
      `contacts/${contactId}/notes/${noteId}/`
    );
    return response.data;
  },

  /**
   * Fixar/Desfixar nota
   * @param {number} contactId - ID do contato
   * @param {number} noteId - ID da nota
   */
  togglePinNote: async (contactId, noteId) => {
    const response = await api.patch(
      `contacts/${contactId}/notes/${noteId}/pin/`
    );
    return response.data;
  },

  // ==========================================
  // SESSÕES DE ATENDIMENTO
  // ==========================================

  /**
   * Listar sessões do contato
   * @param {number} contactId - ID do contato
   * @param {object} params - Filtros (status)
   */
  getSessions: async (contactId, params = {}) => {
    const response = await api.get(`contacts/${contactId}/sessions/`, {
      params,
    });
    return response.data;
  },

  /**
   * Buscar sessão ativa do contato
   * @param {number} contactId - ID do contato
   */
  getCurrentSession: async (contactId) => {
    const response = await api.get(
      `contacts/${contactId}/sessions/current/`
    );
    return response.data;
  },

  // ==========================================
  // ESTATÍSTICAS
  // ==========================================

  /**
   * Buscar estatísticas de atendimento
   * @param {number} days - Período em dias (default: 30)
   */
  getStats: async (days = 30) => {
    const response = await api.get("service-sessions/stats/", {
      params: { days },
    });
    return response.data;
  },

  // ==========================================
  // ATENDENTES E DEPARTAMENTOS
  // ==========================================

  /**
   * Listar atendentes disponíveis
   */
  getAttendants: async () => {
    const response = await api.get(USERS_URL, {
      params: {
        is_active: 1,
        superuser_level__gte: 4,
        simple: 1,
      },
    });
    return response.data;
  },

  /**
   * Listar departamentos ativos
   */
  getDepartments: async () => {
    const response = await api.get("/user-departments/", {
      params: {
        is_active: true,
        simple: true,
      },
    });
    return response.data;
  },

  // ==========================================
  // CONTROLE DO BOT
  // ==========================================

  /**
   * Buscar status do bot para um contato
   * @param {string} phone - Telefone do contato (sem caracteres especiais)
   */
  getBotStatus: async (phone) => {
    const params = new URLSearchParams({ phone: phone });
    const response = await api.get(`${AI_URL}get_bot_status?${params}`);
    return response.data;
  },

  /**
   * Atualizar status do bot
   * @param {string} phone - Telefone do contato (sem caracteres especiais)
   * @param {number} status - 0 = inativo, 1 = ativo
   * @param {string} uid - UID da conta WhatsApp
   */
  updateBotStatus: async (phone, status, uid) => {
    const response = await api.post(`${AI_URL}enable_or_disable/`, {
      action: "enable_or_disable_bot",
      phone: phone,
      uid: uid,
      bot_status: status,
    });
    return response.data;
  },

  /**
   * Resetar memória completa do agente para um cliente
   * @param {string} session - Telefone da sessão/agente (ex: '554731704523')
   * @param {string} phone - Telefone do cliente (ex: '554796897795')
   */
  resetAgentMemory: async (session, phone) => {
    const response = await api.post(`${API_URL}agents_flow/agents/reset-client-memory/`, {
      session,
      phone,
    });
    return response.data;
  },

  // ==========================================
  // CONTATO
  // ==========================================

  /**
   * Buscar detalhes do contato
   * @param {number} contactId - ID do contato
   */
  getContact: async (contactId) => {
    const response = await api.get(`contacts/${contactId}/`);
    return response.data;
  },

  /**
   * Atualizar contato
   * @param {number} contactId - ID do contato
   * @param {object} data - Dados a atualizar
   */
  updateContact: async (contactId, data) => {
    const response = await api.patch(`contacts/${contactId}/`, data);
    return response.data;
  },
};

export default ServiceCenterAPI;
